import React, { useState, useEffect } from 'react';
import { API } from "aws-amplify";
import swal from '@sweetalert/with-react';

import Button from '../../Button';
import ScheduleGrid from '../ScheduleGrid';
import ScheduleGridItem from '../ScheduleGridItem';
import EditPeriod from '../modals/edit_period';

import { formatTime, getData } from '../../../js/Helpers';
import { buildDaySchedule, calculateTimeGridColumnStartEnd, getTimeGridConflictKeys } from '../utils/scheduleGridUtils';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faTimes } from '@fortawesome/free-solid-svg-icons';

import '../../../css/magnetboard.css';

const getPeriodColor = (period) => {
	if(period.is_lunch === '1') return 'pastel-yellow';
	if(period.is_special === '1') return 'green';
	if(period.is_pullout === '1') return 'orange';
	return 'dark-purple';
};

export default function SpecialsPeriods({ subscreenLocked, subscreenRestricted, schoolInfo, scheduleInfo, setScheduleInfo, periods, setPeriods, periodsLoading, handleMoveToNextScreen }) {
	const [isLoading, setIsLoading] = useState(true);

	//////////////////////////
	///// DATA VARIABLES /////
	//////////////////////////
	const [periodID, setPeriodID] = useState(null);
	const [periodType, setPeriodType] = useState(null);
	const [dayNumber, setDayNumber] = useState(null);

	const [sidePanelClosing, setSidePanelClosing] = useState(false);

	const [days, setDays] = useState([]);
	
	/////////////////////////////////
	///// MAGNETBOARD FUNCTIONS /////
	/////////////////////////////////
	const updateNumberDays = async (update_type) => {
		const options =  {
			title: "Are you sure?",
			icon: "warning",
			dangerMode: true,
			buttons: {
				cancel: {
					text: "Cancel",
					value: false,
					visible: true,
					className: 'gray-btn'
				},
				confirm: {
					text: "Yes",
					value: true,
					visible: true,
					className: 'red-btn'
				},
			},
			content: (
				<div>
					<p>Are you sure you want to remove this day? This will permanently delete all periods set up on this day.</p>
				</div>
			)
		}

		if(update_type === 'add' || await swal(options))
		{
			/// Update the FRONTEND ///
			let num_days_in_cycle = scheduleInfo.num_days_in_cycle;
			const new_num_days_in_cycle = (update_type === 'add') ? (parseInt(num_days_in_cycle) + 1) : ((update_type === 'delete') ? (parseInt(num_days_in_cycle) -1) : num_days_in_cycle);

			scheduleInfo.num_days_in_cycle = new_num_days_in_cycle;
			setScheduleInfo({...scheduleInfo});

			/// Update the BACKEND ///
			const data = {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, update_type:update_type};
			const apiName = process.env.REACT_APP_ELEM_ENDPOINT_NAME;
			const url = '/elementary/update-number-days';
			const myInit = { // OPTIONAL
				response: true,
				body: JSON.stringify(data),
			};
		
			try {
				await API.post(apiName, url, myInit);
			} catch(e)
			{
				console.log(e.response);
			}
		}
	}

	const createNewPeriod = (period_type = null, _teacher_id = null, day_number = null) => {
		toggleEditPeriod('new', period_type, day_number);
	}

	const toggleEditPeriod = (period_id, period_type = null, day_number = null) => {
		setPeriodType(period_type);
		setDayNumber(day_number);
		setPeriodID(period_id);
	}

	const sendToDelete = async (period_id) => {
		/// Update the BACKEND ///
		const data = {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, period_id:period_id};
		const apiName = process.env.REACT_APP_ELEM_ENDPOINT_NAME;
		const url = '/elementary/delete-period';
		const myInit = { // OPTIONAL
			response: true,
			body: JSON.stringify(data),
		};
	
		try {
			await API.post(apiName, url, myInit);
		} catch(e)
		{
			console.log(e.response);
		}
	}

	const refreshPeriodsFromBackend = async () => {
		const refreshed_periods = await getData('periods', '/elementary/get-periods', {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id}, true, 'elementary');
		setPeriods(refreshed_periods);
	}

	const removePeriod = async (period_id, removeOptions = {}) => {
		const { skipConfirm = false } = removeOptions;
		const options =  {
			title: "Are you sure?",
			icon: "warning",
			dangerMode: true,
			buttons: {
				cancel: {
					text: "Cancel",
					value: false,
					visible: true,
					className: 'gray-btn'
				},
				confirm: {
					text: "Yes",
					value: true,
					visible: true,
					className: 'red-btn'
				},
			},
			content: (
				<div>
					<p>Are you sure you want to remove this period?</p>
				</div>
			)
		}

		if(skipConfirm || await swal(options))
		{
			/// Update the FRONTEND ///
			const new_periods = periods.filter(period => period.period_id !== period_id);
			setPeriods([...new_periods]);
			toggleEditPeriod(null);

			// Update the BACKEND
			await sendToDelete(period_id);
			await refreshPeriodsFromBackend();
		}
	}

	const handleRemovePeriod = async (period_id, day_number, removeScope = 'day', e = null) => {
		if(e && e.stopPropagation) e.stopPropagation();

		const options =  {
			title: "Are you sure?",
			icon: "warning",
			dangerMode: true,
			buttons: {
				cancel: {
					text: "Cancel",
					value: false,
					visible: true,
					className: 'gray-btn'
				},
				confirm: {
					text: "Yes",
					value: true,
					visible: true,
					className: 'red-btn'
				},
			},
			content: (
				<div>
					<p>Are you sure you want to remove this period?</p>
				</div>
			)
		}

		if(await swal(options))
		{
			if(removeScope === 'all')
			{
				await removePeriod(period_id, { skipConfirm:true });
				return;
			}

			// Decide if you need to delete the entire period or just one day
			const period_index = periods.findIndex(period => period.period_id === period_id);
			const period_data = (period_index !== -1) ? periods[period_index] : null;
			const period_days = (period_data) ? Array.from(new Set((period_data.days || []).map((day) => `${day}`))) : [];
			const period_days_without_this_day = period_days.filter(day => `${day}` !== `${day_number}`);

			if(period_days_without_this_day.length === 0)
			{
				// Update FRONTEND 
				const new_periods = periods.filter(period => period.period_id !== period_id);
				setPeriods([...new_periods]);

				// Update BACKEND
				await sendToDelete(period_id);
			}
			else
			{
				// Update FRONTEND
				periods[period_index].days = period_days_without_this_day;
				setPeriods([...periods]);

				const data = {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, period_data:periods[period_index]};
				const apiName = process.env.REACT_APP_ELEM_ENDPOINT_NAME;
				const url = '/elementary/save-period';
				const myInit = { // OPTIONAL
					response: true,
					body: JSON.stringify(data),
				};
			
				try {
					await API.post(apiName, url, myInit);
				} catch(e)
				{
					console.log(e.response);
				}
			}

			await refreshPeriodsFromBackend();

			toggleEditPeriod(null);
		}
	}

	////////////////////////////////
	///// USE EFFECT FUNCTIONS /////
	////////////////////////////////
	useEffect(() => {
		const num_days_in_cycle = scheduleInfo.num_days_in_cycle;

		let new_days = [];

		for(let day = 1; day <= num_days_in_cycle; day++)
		{
			new_days.push(`${day}`);
		}

		setDays(new_days);
	}, [scheduleInfo]);

	useEffect(() => {
		if(!periodsLoading) setIsLoading(false);
	}, [periodsLoading])
	
	//console.log({scheduleInfo});
	//console.log({dayNumber, periodID});
	//console.log({periods});

	////////////////////////////////
	///// INITIALIZE VARIABLES /////
	////////////////////////////////
	const buildDayPeriods = (dayNumber) => buildDaySchedule({
		items: periods,
		dayNumber,
		lengthKey: 'period_length',
		filterItem: (period, tempDayNumber) => period.days.includes(tempDayNumber) && !period.new
	});

	const buildDayFloatingPeriods = (dayNumber) => buildDaySchedule({
		items: periods,
		dayNumber,
		lengthKey: 'period_length',
		filterItem: (period, tempDayNumber) => period.days.includes(tempDayNumber) && !!period.new
	});

	const getDayPeriodConflictKeys = (dayNumber) => {
		const dayPeriods = buildDaySchedule({
			items: periods,
			dayNumber,
			lengthKey: 'period_length',
			filterItem: (period, tempDayNumber) => period.days.includes(tempDayNumber)
		});
		return getTimeGridConflictKeys(dayPeriods, (period) => `${period.period_id}-${dayNumber}`);
	};

	return (
		<>
		{(subscreenRestricted) ?
		(
			<div className='general-screen-message'>
				<FontAwesomeIcon className='dark-blue-text' style={{fontSize:'60px'}} icon={faLock}/>
				<h1>No Access</h1>
				<div>You do not have access to this screen</div>	
			</div>
		):(subscreenLocked) ?
		(
			<div className='general-screen-message'>
				<FontAwesomeIcon className='dark-blue-text' style={{fontSize:'60px'}} icon={faLock}/>
				<h1>Temporarily Locked</h1>
				<div>This screen is locked until all other prior steps are completed</div>	
			</div>
		):(isLoading) ?
		(
			<div className='center-text'>
				<img src={require('../../../images/balls.gif')} alt='Loading...' style={{height:'25px'}}/>
			</div>
		):
		(
			<>
			<EditPeriod schoolInfo={schoolInfo} periodID={periodID} periodType={periodType} dayNumber={dayNumber} days={days} periods={periods} setPeriods={setPeriods} removePeriod={removePeriod} toggleEditPeriod={toggleEditPeriod} sidePanelClosing={sidePanelClosing} setSidePanelClosing={setSidePanelClosing}/>
			<div className='mb-elem-container'>
				<div className='mb-elem-top-bar'>
					<div className='mb-top-bar-inner'>
						<div className='mb-elem-schedule-name-container'>
							<h1>Specials Periods</h1>
						</div>
						<p className='progress-subheader elem-manage-homerooms-subheader'>Use this screen to set up the blocks of time when specials can run.</p>
					</div>
				</div>
				<div className='btn blue-btn align-right' style={{width:'fit-content'}} onClick={() => updateNumberDays('add')}>Add a Day</div>
				<div>
					<div className='mb-elem-teacher-schedule'>
						<ScheduleGrid
							days={days}
							getDayKey={(day_number) => `${day_number}`}
							getDayLabel={(day_number) => `Day ${day_number}`}
							getDayItems={buildDayPeriods}
							getDayFloatingItems={buildDayFloatingPeriods}
							onDayBodyClick={(day_number) => createNewPeriod('special', null, day_number)}
							renderDayHeaderExtra={(day_number, { isLastDay }) => (
								isLastDay ? <div className='red-link full-width text-align-center small-text' onClick={() => updateNumberDays('delete')} style={{position:'absolute',top:'43px'}}>Remove Day</div> : null
							)}
							renderItem={(period, { day, dayItems, loggedPositions }) => {
								const dayConflictKeys = getDayPeriodConflictKeys(day);
								const period_id = period.period_id;
								const period_name = period.period_name;
								const start_time = period.start_time;
								const end_time = period.end_time;
								const start_time_formatted = formatTime(start_time);
								const end_time_date_formatted = formatTime(end_time);
								const row_start = period.row_start;
								const row_end = period.row_end;
								const period_color = getPeriodColor(period);
								const period_position = calculateTimeGridColumnStartEnd(period, dayItems, loggedPositions, start_time, end_time, 'period_id');
								loggedPositions[period_id] = period_position.position_index;

								return (
									<ScheduleGridItem
										key={`${period_id}-${day}`}
										itemKey={period_id}
										position={period_position}
										rowStart={row_start}
										rowEnd={row_end}
										color={period_color}
										temporary={!!period.new}
										conflict={dayConflictKeys.has(`${period_id}-${day}`)}
										selected={periodID === period_id}
										onClick={!periodID ? () => toggleEditPeriod(period_id) : null}
										name={period_name}
										nameTooltipText={period_name}
										time={`${start_time_formatted} - ${end_time_date_formatted}`}
										timeTooltipText={`${start_time_formatted} - ${end_time_date_formatted}`}
										headerMenuOptions={(Array.isArray(period.days) && period.days.length > 1) ? [
											{ label:'Remove This Day', onClick:(e) => handleRemovePeriod(period_id, day, 'day', e) },
											{ label:'Remove All Days', onClick:(e) => handleRemovePeriod(period_id, day, 'all', e) }
										] : []}
										headerAction={(Array.isArray(period.days) && period.days.length > 1) ? null : <FontAwesomeIcon className='mb-elem-remove-block small-text cursor-pointer' icon={faTimes} onClick={(e) => handleRemovePeriod(period_id, day, 'all', e)}/>}
									/>
								)
							}}
							renderFloatingItem={(period, { day, dayItems, dayFloatingItems, loggedPositions }) => {
								const dayConflictKeys = getDayPeriodConflictKeys(day);
								const period_id = period.period_id;
								const period_name = period.period_name;
								const start_time = period.start_time;
								const end_time = period.end_time;
								const start_time_formatted = formatTime(start_time);
								const end_time_date_formatted = formatTime(end_time);
								const period_color = getPeriodColor(period);
								const period_position = calculateTimeGridColumnStartEnd(period, [...dayItems, ...dayFloatingItems], loggedPositions, start_time, end_time, 'period_id');
								loggedPositions[period_id] = period_position.position_index;

								return (
									<ScheduleGridItem
										key={`${period_id}-${day}-floating`}
										itemKey={`${period_id}-floating`}
										position={period_position}
										rowStart={period.row_start}
										rowEnd={period.row_end}
										color={period_color}
										temporary={true}
										floating={true}
										conflict={dayConflictKeys.has(`${period_id}-${day}`)}
										name={period_name}
										nameTooltipText={period_name}
										time={`${start_time_formatted} - ${end_time_date_formatted}`}
										timeTooltipText={`${start_time_formatted} - ${end_time_date_formatted}`}
									/>
								);
							}}
							onAddItem={(day_number) => createNewPeriod('special', null, day_number)}
							addItemLabel='Add Period'
							addItemClassName='medium-text'
						/>
					</div>				
				</div>
			</div>
			<div className='fixed-bottom-save-container'>
				<Button classes={['btn', 'btn-extra-large', 'green-btn', 'align-right', 'move-to-next-screen-btn']} selected={false} text='Go to Next Step: Specials Courses' onClickFunction={handleMoveToNextScreen} />
			</div>
			</>
		)
		}
		</>
	)
}
